import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Search, X, KeyRound, Power, Edit3 } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  "msp_owner",
  "service_manager",
  "service_desk",
  "technician",
  "readonly",
  "client_user",
  "end_user",
  "l1_technician",
  "admin",
];

export default function Users() {
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [password, setPassword] = useState("");

  const emptyForm = {
    name: "",
    email: "",
    password: "",
    role: "technician",
    title: "",
    phone: "",
    client_id: "",
    active: true,
  };

  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    const [u, c] = await Promise.all([api.get("/users"), api.get("/clients")]);
    setUsers(u.data);
    setClients(c.data);
  };

  useEffect(() => { load(); }, []);

  const clientMap = useMemo(() => Object.fromEntries(clients.map(c => [c.id, c])), [clients]);

  const filtered = users.filter(u => {
    const hay = `${u.name || ""} ${u.email || ""} ${u.role || ""} ${clientMap[u.client_id]?.name || ""}`.toLowerCase();
    return !q || hay.includes(q.toLowerCase());
  });

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      name: u.name || "",
      email: u.email || "",
      password: "",
      role: u.role || "technician",
      title: u.title || "",
      phone: u.phone || "",
      client_id: u.client_id || "",
      active: u.active !== false,
    });
    setShowForm(true);
  };

  const saveUser = async () => {
    try {
      if (!form.name.trim()) return toast.error("Name is required");
      if (!editing && !form.email.trim()) return toast.error("Email is required");

      if (editing) {
        await api.patch(`/users/${editing.id}`, {
          name: form.name,
          role: form.role,
          title: form.title,
          phone: form.phone,
          client_id: form.client_id,
          active: form.active,
        });
        toast.success("User updated");
      } else {
        if (!form.password || form.password.length < 8) return toast.error("Temporary password must be at least 8 characters");
        await api.post("/users", form);
        toast.success("User created");
      }

      setShowForm(false);
      setEditing(null);
      load();
    } catch (err) {
      console.error("Save user failed:", err);
      toast.error(err?.response?.data?.detail || err?.message || "User save failed");
    }
  };

  const disableUser = async (u) => {
    if (!window.confirm(`Disable ${u.name || u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User disabled");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Disable failed");
    }
  };

  const resetPassword = async () => {
    if (!resetting) return;
    if (!password || password.length < 8) return toast.error("Password must be at least 8 characters");

    try {
      await api.post(`/users/${resetting.id}/reset-password`, { password });
      toast.success("Password reset");
      setResetting(null);
      setPassword("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Password reset failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col xl:flex-row xl:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Administration</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">User Management ({filtered.length})</h1>
          <p className="text-sm text-zinc-500 mt-1">Create MSP users, technicians, service desk staff, read-only users, and client portal users.</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button onClick={openNew} className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-2 text-sm flex items-center gap-2">
            <Plus size={15} /> New User
          </button>

          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search users" className="bg-zinc-950 border border-zinc-800 rounded-md pl-9 pr-3 py-2 text-sm w-72 focus:outline-none focus:border-blue-500/60" />
          </div>
        </div>
      </div>

      {showForm && (
        <div className="bg-zinc-950 border border-blue-900/50 rounded-md p-5 shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-display text-lg font-semibold">{editing ? "Edit User" : "Create New User"}</div>
              <div className="text-xs text-zinc-500 mt-1">{editing ? "Update role, client assignment, and active status." : "Create a service desk, technician, MSP admin, or client portal account."}</div>
            </div>
            <button onClick={() => setShowForm(false)} className="text-zinc-500 hover:text-zinc-100"><X size={18}/></button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <Field label="Name">
              <input value={form.name} onChange={e=>setForm({...form, name:e.target.value})} className="input" placeholder="Full name" />
            </Field>

            <Field label="Email">
              <input value={form.email} disabled={!!editing} onChange={e=>setForm({...form, email:e.target.value})} className="input disabled:opacity-50" placeholder="user@example.com" />
            </Field>

            {!editing && (
              <Field label="Temporary Password">
                <input type="password" value={form.password} onChange={e=>setForm({...form, password:e.target.value})} className="input" placeholder="Minimum 8 characters" />
              </Field>
            )}

            <Field label="Role">
              <select value={form.role} onChange={e=>setForm({...form, role:e.target.value})} className="input">
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>

            <Field label="Client Assignment">
              <select value={form.client_id} onChange={e=>setForm({...form, client_id:e.target.value})} className="input">
                <option value="">No client / MSP internal</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>

            <Field label="Title">
              <input value={form.title} onChange={e=>setForm({...form, title:e.target.value})} className="input" placeholder="Technician, Service Manager..." />
            </Field>

            <Field label="Phone">
              <input value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})} className="input" placeholder="+63..." />
            </Field>

            <Field label="Active">
              <select value={form.active ? "true" : "false"} onChange={e=>setForm({...form, active:e.target.value === "true"})} className="input">
                <option value="true">Active</option>
                <option value="false">Disabled</option>
              </select>
            </Field>
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="bg-zinc-900 border border-zinc-800 rounded px-4 py-2 text-sm">Cancel</button>
            <button onClick={saveUser} className="bg-blue-600 hover:bg-blue-700 rounded px-4 py-2 text-sm text-white">{editing ? "Save Changes" : "Create User"}</button>
          </div>
        </div>
      )}

      {resetting && (
        <div className="bg-zinc-950 border border-amber-900/60 rounded-md p-5">
          <div className="font-display text-lg font-semibold">Reset Password</div>
          <div className="text-xs text-zinc-500 mt-1">Set a new temporary password for {resetting.name || resetting.email}.</div>

          <div className="mt-4 flex gap-2">
            <input type="password" value={password} onChange={e=>setPassword(e.target.value)} className="input max-w-md" placeholder="New password, minimum 8 characters" />
            <button onClick={resetPassword} className="bg-amber-600 hover:bg-amber-700 rounded px-4 py-2 text-sm text-white">Reset</button>
            <button onClick={() => { setResetting(null); setPassword(""); }} className="bg-zinc-900 border border-zinc-800 rounded px-4 py-2 text-sm">Cancel</button>
          </div>
        </div>
      )}

      <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/50 border-b border-zinc-900 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Client</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Created</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.id} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                <td className="px-4 py-2 font-medium">{u.name || "—"}</td>
                <td className="px-4 py-2 font-mono text-xs text-zinc-400">{u.email}</td>
                <td className="px-4 py-2 font-mono text-xs">{u.role}</td>
                <td className="px-4 py-2 text-zinc-400">{clientMap[u.client_id]?.name || "MSP Internal"}</td>
                <td className="px-4 py-2">
                  <span className={u.active === false ? "text-red-300 bg-red-950 border border-red-900/60 rounded px-2 py-1 text-xs" : "text-emerald-300 bg-emerald-950 border border-emerald-900/60 rounded px-2 py-1 text-xs"}>
                    {u.active === false ? "disabled" : "active"}
                  </span>
                </td>
                <td className="px-4 py-2 font-mono text-[11px] text-zinc-500">{u.created_at?.slice(0,10) || "—"}</td>
                <td className="px-4 py-2">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => openEdit(u)} className="bg-zinc-900 border border-zinc-800 hover:border-blue-700 rounded px-2 py-1.5 text-zinc-300" title="Edit">
                      <Edit3 size={13}/>
                    </button>
                    <button onClick={() => setResetting(u)} className="bg-zinc-900 border border-zinc-800 hover:border-amber-700 rounded px-2 py-1.5 text-zinc-300" title="Reset password">
                      <KeyRound size={13}/>
                    </button>
                    <button onClick={() => disableUser(u)} className="bg-red-950/40 border border-red-900/60 hover:bg-red-900/60 rounded px-2 py-1.5 text-red-300" title="Disable">
                      <Power size={13}/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-sm text-zinc-500">No users found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <style>{`
        .input {
          width: 100%;
          background: rgb(24 24 27);
          border: 1px solid rgb(39 39 42);
          border-radius: 0.375rem;
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          outline: none;
        }
        .input:focus {
          border-color: rgba(59, 130, 246, 0.7);
        }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">{label}</div>
      {children}
    </label>
  );
}
